import { MessageBus } from '@bs/utils';
import { ARIA, CLASS_NAMES, KEY_CODES, SELECTORS, EVENTS } from '@bs/constants';

const COLLAPSED_SIZE = '44px';
const EXPANDED_WIDTH_FIXED = '282px';
const EXPANDED_WIDTH_VARIABLE = '95%';
const EXPANDED_WIDTH_MOBILE = '85%';
const MOBILE_QUERY = '(max-width: 575.98px)';

/**
 * A class for the Image Caption module.
 *
 * Expand/collapse behaviour mirrors ListAndImage:
 *  - openCaption  measures the real content height and animates width + height
 *    from the collapsed pill (44×44) to the expanded dimensions. The expanded
 *    width is a fixed 282px, or 95% of the container when the twig include is
 *    passed `variable_width: true` (which adds the --variable-width modifier).
 *    Below 576px both variants use 75% instead, so the caption can't outgrow a
 *    narrow parent. These targets mirror the SCSS exactly. The caption text is
 *    only faded in once the width transition has finished, so it never shifts
 *    while the box is still growing.
 *  - closeCaption pins the current px dimensions, then transitions back to
 *    44×44 before removing IS_OPEN via a transitionend guard on 'width'.
 *  - Outside clicks and Escape key close the caption.
 *
 * Requires CLASS_NAMES.IS_CONTENT_VISIBLE ('is-content-visible') and
 * CLASS_NAMES.IMAGE_CAPTION_VARIABLE_WIDTH ('image-caption--variable-width').
 */
export default class ImageCaption extends MessageBus {
  constructor(element) {
    super();
    this.element = element;
    this.handleOpenTransitionEnd = null;
    this.init();
  }

  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  cacheDomReferences() {
    this.trigger = this.element.querySelector(SELECTORS.IMAGE_CAPTION_TRIGGER);
    this.content = this.element.querySelector(SELECTORS.IMAGE_CAPTION_CONTENT);
    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.isVariableWidth = this.element.classList.contains(
      CLASS_NAMES.IMAGE_CAPTION_VARIABLE_WIDTH
    );

    return this;
  }

  setupHandlers() {
    this.handleTriggerClick = this.onTriggerClick.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleDocumentKeydown = this.onDocumentKeydown.bind(this);

    return this;
  }

  enable() {
    if (this.trigger) {
      this.trigger.addEventListener(EVENTS.CLICK, this.handleTriggerClick);
    }

    document.addEventListener(EVENTS.KEY_DOWN, this.handleDocumentKeydown);

    return this;
  }

  /**
   * Removes every listener this instance registered. Needed so cloned captions
   * created by HeroSlider can be torn down without leaking document listeners.
   * @returns {ImageCaption} The ImageCaption instance (for chaining).
   */
  destroy() {
    if (this.trigger) {
      this.trigger.removeEventListener(EVENTS.CLICK, this.handleTriggerClick);
    }

    this.clearOpenTransitionEnd();

    document.removeEventListener(EVENTS.KEY_DOWN, this.handleDocumentKeydown);
    document.removeEventListener(EVENTS.CLICK, this.handleDocumentClick);

    return this;
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  onTriggerClick(event) {
    event.stopPropagation();

    const isOpen = this.element.classList.contains(CLASS_NAMES.IS_OPEN);

    if (isOpen) {
      this.closeCaption();
    } else {
      this.openCaption();
    }

    return this;
  }

  onDocumentClick(event) {
    const clickedInside = event.target.closest(
      SELECTORS.IMAGE_CAPTION_COMPONENT
    );

    if (!clickedInside) {
      this.closeCaption();
    }

    return this;
  }

  onDocumentKeydown(event) {
    if (event.key !== KEY_CODES.IMAGE_CAPTION_ESCAPE) return this;

    this.closeCaption();

    return this;
  }

  // ─── Open ─────────────────────────────────────────────────────────────────

  /**
   * Measures the caption's natural expanded height and animates from the
   * collapsed 44×44 pill to the expanded dimensions, then adds IS_OPEN. The
   * text is revealed separately once the box has finished expanding.
   * @returns {ImageCaption} The ImageCaption instance (for chaining).
   */
  openCaption() {
    // Drop any handler left over from an interrupted open
    this.clearOpenTransitionEnd();
    this.element.classList.remove(CLASS_NAMES.IS_CONTENT_VISIBLE);

    const expandedWidth = this.getExpandedWidth();

    // 1. Set the target width with no transition so the height is accurate.
    //    Measuring at the real target width matters wherever the box is
    //    percentage-based, since the text wraps differently per container.
    this.element.style.transition = 'none';
    this.element.style.width = expandedWidth;
    this.element.style.height = 'auto';

    // Add IS_OPEN temporarily so __content participates in layout for measurement
    this.element.classList.add(CLASS_NAMES.IS_OPEN);

    const naturalHeight = this.content.offsetHeight;

    // Remove before snapping back — re-added after reflow below
    this.element.classList.remove(CLASS_NAMES.IS_OPEN);

    // 2. Snap back to collapsed size (no transition yet)
    this.element.style.width = COLLAPSED_SIZE;
    this.element.style.height = COLLAPSED_SIZE;

    // 3. Force reflow so the browser registers the collapsed dimensions
    this.element.getBoundingClientRect();

    // 4. Re-enable transition and animate to target dimensions
    this.element.style.transition = '';
    this.element.style.width = expandedWidth;
    this.element.style.height = `${naturalHeight}px`;

    // Clear any visibility set during a previous close
    this.content.style.visibility = '';

    this.element.classList.add(CLASS_NAMES.IS_OPEN);
    this.element.setAttribute(SELECTORS.ARIA_EXPANDED, ARIA.TRUE);

    // 5. Fade the text in only once the box has finished expanding
    this.handleOpenTransitionEnd = (event) => {
      if (event.propertyName !== 'width') return;

      this.element.classList.add(CLASS_NAMES.IS_CONTENT_VISIBLE);
      this.clearOpenTransitionEnd();
    };

    this.element.addEventListener(
      EVENTS.TRANSITIONEND,
      this.handleOpenTransitionEnd
    );

    // Register outside-click listener now that the caption is open
    document.addEventListener(EVENTS.CLICK, this.handleDocumentClick);

    return this;
  }

  // ─── Close ────────────────────────────────────────────────────────────────

  /**
   * Pins the current px dimensions then animates back to 44×44, removing
   * IS_OPEN only after the width transition has finished.
   * @returns {ImageCaption} The ImageCaption instance (for chaining).
   */
  closeCaption() {
    if (!this.element.classList.contains(CLASS_NAMES.IS_OPEN)) return this;

    // The box may still be opening — don't let the pending reveal fire
    this.clearOpenTransitionEnd();
    document.removeEventListener(EVENTS.CLICK, this.handleDocumentClick);

    // Hide the content instantly so it doesn't crunch visibly as the
    // container collapses. visibility:hidden keeps it in the flow so the
    // container still has the correct dimensions to animate from.
    this.element.classList.remove(CLASS_NAMES.IS_CONTENT_VISIBLE);
    this.content.style.visibility = 'hidden';

    // Pin current dimensions explicitly so the transition has a from-value.
    // Measured, not assumed — the open width may be a percentage.
    const { width, height } = this.element.getBoundingClientRect();

    this.element.style.transition = 'none';
    this.element.style.width = `${width}px`;
    this.element.style.height = `${height}px`;

    // Force reflow
    this.element.getBoundingClientRect();

    // Animate to collapsed size
    this.element.style.transition = '';
    this.element.style.width = COLLAPSED_SIZE;
    this.element.style.height = COLLAPSED_SIZE;

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'width') return;

      this.element.classList.remove(CLASS_NAMES.IS_OPEN);
      this.element.setAttribute(SELECTORS.ARIA_EXPANDED, ARIA.FALSE);
      this.element.removeEventListener(EVENTS.TRANSITIONEND, onTransitionEnd);
    };

    this.element.addEventListener(EVENTS.TRANSITIONEND, onTransitionEnd);

    return this;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Resolves the expanded width for the current viewport and variant. Evaluated
   * on every open rather than cached, so a resize or orientation change between
   * opens picks up the right target.
   * @returns {string} A CSS width value.
   */
  getExpandedWidth() {
    if (this.mobileQuery.matches) return EXPANDED_WIDTH_MOBILE;

    return this.isVariableWidth
      ? EXPANDED_WIDTH_VARIABLE
      : EXPANDED_WIDTH_FIXED;
  }

  /**
   * Removes any pending open transitionend listener, so a close (or re-open)
   * landing mid-animation can't be followed by a stale text reveal.
   * @returns {ImageCaption} The ImageCaption instance (for chaining).
   */
  clearOpenTransitionEnd() {
    if (this.handleOpenTransitionEnd) {
      this.element.removeEventListener(
        EVENTS.TRANSITIONEND,
        this.handleOpenTransitionEnd
      );
      this.handleOpenTransitionEnd = null;
    }

    return this;
  }
}
